import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import { Filter, ChevronLeft, ChevronRight, Eye, AlertTriangle } from "lucide-react";
import {
  ADMISSION_STAGES, STAGE_COLORS, PRIORITY_COLORS, SOURCE_LABELS,
  stageOf, daysSince, isOverdue, fmtDate,
} from "@/lib/crm";

const SOURCES = [
  { v: "", l: "All Sources" },
  { v: "ai_counseling", l: "AI Counseling" },
  { v: "expert_counseling", l: "Expert Counseling" },
  { v: "contact_form", l: "Contact" },
  { v: "college_page_cta", l: "College CTA" },
];

export default function AdminLeads() {
  const [data, setData] = useState({ leads: [], total: 0, page: 1, perPage: 20 });
  const [source, setSource] = useState("");
  const [stage, setStage] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { page };
    if (source) params.source = source;
    if (stage) params.stage = stage;
    api.get("/admin/leads", { params }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [source, stage, page]);

  const totalPages = Math.max(1, Math.ceil(data.total / data.perPage));

  return (
    <div data-testid="admin-leads-page">
      <SEOHead title="Leads" />
      <h1 className="font-display text-2xl font-bold text-slate-900">Leads</h1>
      <p className="text-sm text-slate-500">{data.total} total leads</p>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-sm text-slate-500"><Filter className="h-4 w-4" /> Filter:</div>
        <select data-testid="leads-filter-source" value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }} className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[#0B2A6B]">
          {SOURCES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
        <select data-testid="leads-filter-stage" value={stage} onChange={(e) => { setStage(e.target.value); setPage(1); }} className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[#0B2A6B]">
          <option value="">All Stages</option>
          {ADMISSION_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[1100px] text-left">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <th className="py-3.5 pl-5 pr-4">Lead</th>
              <th className="px-4 py-3.5">Stage</th>
              <th className="px-4 py-3.5">Counsellor</th>
              <th className="px-4 py-3.5">Priority</th>
              <th className="px-4 py-3.5">Score</th>
              <th className="px-4 py-3.5">Next Follow-up</th>
              <th className="px-4 py-3.5">Last Contact</th>
              <th className="px-4 py-3.5">Idle</th>
              <th className="px-4 py-3.5 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="p-10 text-center text-slate-400">Loading…</td></tr>
            ) : data.leads.length === 0 ? (
              <tr><td colSpan={9} className="p-10 text-center text-slate-400" data-testid="leads-empty">No leads found.</td></tr>
            ) : (
              data.leads.map((l) => {
                const st = stageOf(l);
                const overdue = isOverdue(l.nextFollowUp, st);
                const idle = daysSince(l.lastContactDate || l.createdAt);
                const accent = l.priority === "High" ? "#EF4444" : l.priority === "Low" ? "#CBD5E1" : "#D4A24C";
                const sc = l.leadScore ?? 0;
                const scoreColor = sc >= 75 ? "#10B981" : sc >= 50 ? "#D4A24C" : "#94A3B8";
                return (
                  <tr key={l.id} className={`border-b border-slate-100 last:border-0 transition-colors hover:bg-slate-50 ${overdue ? "bg-red-50/40" : ""}`} data-testid={`lead-row-${l.id}`}>
                    <td className="py-4 pl-5 pr-4" style={{ boxShadow: `inset 4px 0 0 ${accent}` }}>
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0B2A6B]/10 text-sm font-bold text-[#0B2A6B]">{(l.name || "?")[0]?.toUpperCase()}</span>
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900 truncate">{l.name}</p>
                          <p className="text-xs text-slate-500 truncate">{l.mobile} · {l.courseInterest || "No course"}</p>
                          <span className="mt-0.5 inline-block rounded bg-slate-100 px-1.5 py-px text-[10px] font-medium text-slate-500">{SOURCE_LABELS[l.source] || l.source}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STAGE_COLORS[st]}`}>{st}</span></td>
                    <td className="px-4 py-4 text-sm">
                      {l.counsellorAssigned ? (
                        <span className="inline-flex items-center gap-1.5 text-slate-700">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-600">{l.counsellorAssigned[0]?.toUpperCase()}</span>
                          {l.counsellorAssigned}
                        </span>
                      ) : <span className="text-slate-300">Unassigned</span>}
                    </td>
                    <td className="px-4 py-4"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${PRIORITY_COLORS[l.priority] || PRIORITY_COLORS.Medium}`}>{l.priority || "Medium"}</span></td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-12 overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full rounded-full" style={{ width: `${sc}%`, background: scoreColor }} />
                        </div>
                        <span className="text-xs font-semibold text-slate-600">{sc}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm">
                      {l.nextFollowUp ? (
                        <span className={`inline-flex items-center gap-1 font-medium ${overdue ? "text-red-600" : "text-slate-700"}`}>
                          {overdue && <AlertTriangle className="h-3.5 w-3.5" />}{fmtDate(l.nextFollowUp)}
                        </span>
                      ) : <span className="text-slate-300">Not set</span>}
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600">{fmtDate(l.lastContactDate)}</td>
                    <td className="px-4 py-4 text-sm">
                      {idle != null ? <span className={idle >= 7 ? "font-semibold text-amber-600" : "text-slate-500"}>{idle}d</span> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Link to={`/admin/leads/${l.id}`} data-testid={`lead-view-${l.id}`} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-[#0B2A6B] transition-colors hover:bg-[#0B2A6B] hover:text-white"><Eye className="h-4 w-4" /> Open</Link>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-5 flex items-center justify-between">
        <span className="text-sm text-slate-500">Page {data.page} of {totalPages}</span>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:opacity-40 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /> Prev</button>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:opacity-40 hover:bg-slate-50">Next <ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
}
